/**
 * Aba "Financeiro" do Cliente 360. Espelha o mockup: 4 stats + barra de
 * Recebimento + col-main [Parcelas (filtrável) | Limite de crédito + Condições].
 * Dado real do endpoint GET /clientes/{id}/financeiro (agrega Boletos +
 * Pedidos + limite). Score de crédito / desconto padrão ficam "—" (sem fonte).
 */
import React, { useEffect, useState } from "react";
import { FiClock, FiCreditCard, FiDollarSign, FiTrendingUp } from "react-icons/fi";

import { obterFinanceiroCliente } from "../../../../services/comercial";
import { Badge, Field, fmtBRLCheio, fmtDataCurta, Panel, Stat } from "../cli360";

const STATUS = {
  PAGO: { tone: "success", label: "Paga" },
  PENDENTE: { tone: "warning", label: "Pendente" },
  VENCIDO: { tone: "danger", label: "Vencida" },
};
const FORMA = { BOLETO: "Boleto", PIX: "PIX", CARTAO: "Cartão" };

function parseParcela(desc) {
  const partes = (desc || "").split("·").map((s) => s.trim());
  return { parcela: partes[0] || "—", pedido: partes[1] || "" };
}

function FinanceiroTab({ request, clienteId }) {
  const [fin, setFin] = useState(null); // null = carregando
  const [erro, setErro] = useState(false);
  const [filtro, setFiltro] = useState("all");

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const data = await obterFinanceiroCliente(request, clienteId, { silentError: true });
        if (vivo) setFin(data);
      } catch {
        if (vivo) setErro(true);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [request, clienteId]);

  if (erro) {
    return (
      <section className="tab-panel">
        <div className="panel">
          <div className="panel-body muted sm">Não foi possível carregar o financeiro.</div>
        </div>
      </section>
    );
  }
  if (!fin) {
    return (
      <section className="tab-panel">
        <div className="panel">
          <div className="panel-body muted sm">Carregando…</div>
        </div>
      </section>
    );
  }

  const base = fin.recebido + fin.a_receber + fin.vencido;
  const pct = (v) => (base > 0 ? (v / base) * 100 : 0);
  const parcelas = fin.parcelas || [];
  const fParcelas = filtro === "all" ? parcelas : parcelas.filter((p) => p.situacao === filtro);
  const lim = fin.limite;
  const cond = fin.condicoes || {};

  return (
    <section className="tab-panel">
      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat label="Total contratado" value={fmtBRLCheio(fin.total_contratado)} sub="em pedidos" />
        <Stat label="Recebido" value={fmtBRLCheio(fin.recebido)} sub={`${Math.round(pct(fin.recebido))}% do total`} tone="pos" />
        <Stat label="A receber" value={fmtBRLCheio(fin.a_receber)} sub="parcelas em aberto" />
        <Stat
          label="Vencido"
          value={fmtBRLCheio(fin.vencido)}
          sub={fin.vencido > 0 ? `inadimplência ${fin.inadimplencia_pct}%` : "nada vencido"}
          tone={fin.vencido > 0 ? "danger" : undefined}
          style={fin.vencido > 0 ? { borderColor: "var(--danger-border)" } : undefined}
        />
      </div>

      <Panel
        title="Recebimento"
        icon={<FiTrendingUp aria-hidden="true" />}
        action={<span className="tiny muted">inadimplência {fin.inadimplencia_pct}%</span>}
        style={{ marginBottom: 16 }}
      >
        <div className="panel-body">
          <div style={{ display: "flex", height: 11, borderRadius: 999, overflow: "hidden", background: "var(--surface-2)" }}>
            <div style={{ width: `${pct(fin.recebido)}%`, background: "var(--success-solid)" }} />
            <div style={{ width: `${pct(fin.a_receber)}%`, background: "var(--warning-solid)" }} />
            <div style={{ width: `${pct(fin.vencido)}%`, background: "var(--danger-solid)" }} />
          </div>
          <div className="flex wrap" style={{ gap: 22, marginTop: 14 }}>
            {[
              ["Recebido", "var(--success-solid)", fin.recebido],
              ["A receber", "var(--warning-solid)", fin.a_receber],
              ["Vencido", "var(--danger-solid)", fin.vencido],
            ].map(([l, cor, v]) => (
              <div className="flex center" style={{ gap: 8 }} key={l}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: cor }} />
                <div>
                  <div className="tiny muted">{l}</div>
                  <div className="num" style={{ fontWeight: 600 }}>{fmtBRLCheio(v)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Panel>

      <div className="col-main">
        <Panel
          title="Parcelas"
          sub={`· ${parcelas.length}`}
          icon={<FiCreditCard aria-hidden="true" />}
          action={
            <div className="seg">
              {[["all", "Todas"], ["PENDENTE", "Pendentes"], ["PAGO", "Pagas"], ["VENCIDO", "Vencidas"]].map(([k, l]) => (
                <button key={k} type="button" className={filtro === k ? "on" : ""} onClick={() => setFiltro(k)}>
                  {l}
                </button>
              ))}
            </div>
          }
        >
          <table className="dtable">
            <thead>
              <tr>
                <th>Parcela</th>
                <th>Pedido</th>
                <th>Vencimento</th>
                <th>Forma</th>
                <th className="num">Valor</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {fParcelas.length === 0 ? (
                <tr><td colSpan={6} className="muted sm">Nenhuma parcela.</td></tr>
              ) : (
                fParcelas.map((p) => {
                  const pp = parseParcela(p.descricao);
                  const st = STATUS[p.situacao] || { tone: "neutral", label: p.situacao };
                  return (
                    <tr key={p.id}>
                      <td>{pp.parcela}</td>
                      <td className="cmono">{pp.pedido || "—"}</td>
                      <td className="cmono">{fmtDataCurta(p.vencimento) || "—"}</td>
                      <td>{FORMA[p.meio_pagamento] || p.meio_display}</td>
                      <td className="num">{fmtBRLCheio(p.valor)}</td>
                      <td>
                        <Badge tone={st.tone} dot>{st.label}</Badge>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </Panel>

        <div className="stack" style={{ gap: 16 }}>
          <Panel title="Limite de crédito" icon={<FiDollarSign aria-hidden="true" />}>
            <div className="panel-body">
              {lim ? (
                <>
                  <div className="flex between" style={{ alignItems: "baseline" }}>
                    <div className="num" style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 600, letterSpacing: "-.03em" }}>
                      {fmtBRLCheio(lim.disponivel)}
                    </div>
                    <div className="tiny muted">disponível de {fmtBRLCheio(lim.limite)}</div>
                  </div>
                  <div className="bar lg" style={{ margin: "13px 0 8px" }}>
                    <i style={{ width: `${Math.min(100, lim.percentual)}%`, background: "var(--accent)" }} />
                  </div>
                  <div className="flex between tiny muted">
                    <span>Utilizado {fmtBRLCheio(lim.utilizado)}</span>
                    <span>{Math.round(lim.percentual)}%</span>
                  </div>
                  <div className="divider" />
                  <div className="flex between center">
                    <span className="sm">Score interno de crédito</span>
                    {lim.score != null ? <Badge tone="success">{lim.score}</Badge> : <span className="tiny muted">sem score</span>}
                  </div>
                </>
              ) : (
                <div className="muted sm">Sem limite de crédito definido.</div>
              )}
            </div>
          </Panel>

          <Panel title="Condições" icon={<FiClock aria-hidden="true" />}>
            <div className="panel-body fields" style={{ paddingTop: 6, paddingBottom: 6 }}>
              <Field label="Forma usual" cols="1fr auto">{FORMA[cond.forma_usual] || "—"}</Field>
              <Field label="Parcela média" cols="1fr auto" mono>
                {cond.parcela_media != null ? fmtBRLCheio(cond.parcela_media) : "—"}
              </Field>
              <Field label="Atraso médio" cols="1fr auto">
                {cond.atraso_medio_dias != null ? `${cond.atraso_medio_dias} dias` : "—"}
              </Field>
              <Field label="Desconto padrão" cols="1fr auto">
                {cond.desconto_padrao != null ? `${cond.desconto_padrao}%` : "—"}
              </Field>
            </div>
          </Panel>
        </div>
      </div>
    </section>
  );
}

export default FinanceiroTab;
